import { CLIENT_SIM_MS } from '@enugu/shared';
/**
 * Fixed-timestep simulation + interpolated rendering ("Fix Your Timestep").
 * The sim runs at the server tick rate so client prediction and server
 * simulation advance in identical steps; rendering runs at display refresh
 * and interpolates between the previous and current sim states.
 *
 * `advance(now)` is the testable core; `start()` drives it with rAF.
 */
export class FixedStepLoop {
    stepMs;
    maxSteps;
    maxFrameMs;
    cb;
    acc = 0;
    last = -1;
    rafId = 0;
    running = false;
    tick = 0;
    /** Sim steps dropped because the frame budget was exceeded (stat). */
    droppedSteps = 0;
    constructor(cb, opts = {}) {
        this.cb = cb;
        this.stepMs = opts.stepMs ?? CLIENT_SIM_MS;
        this.maxSteps = opts.maxStepsPerFrame ?? 5;
        this.maxFrameMs = opts.maxFrameMs ?? 250;
    }
    /** Runs due sim steps and one render for timestamp `now` (ms). */
    advance(now) {
        if (this.last < 0)
            this.last = now;
        let frame = now - this.last;
        this.last = now;
        if (frame < 0)
            frame = 0;
        if (frame > this.maxFrameMs)
            frame = this.maxFrameMs;
        this.acc += frame;
        let steps = 0;
        while (this.acc >= this.stepMs) {
            if (steps === this.maxSteps) {
                // Too far behind: drop whole steps but keep the sub-step remainder.
                const drop = Math.floor(this.acc / this.stepMs);
                this.droppedSteps += drop;
                this.acc -= drop * this.stepMs;
                break;
            }
            this.cb.fixedUpdate(this.stepMs / 1000, this.tick++);
            this.acc -= this.stepMs;
            steps++;
        }
        this.cb.render(this.acc / this.stepMs, frame / 1000, now);
    }
    start() {
        if (this.running)
            return;
        this.running = true;
        this.last = -1;
        const frame = (t) => {
            if (!this.running)
                return;
            this.rafId = requestAnimationFrame(frame);
            this.advance(t);
        };
        this.rafId = requestAnimationFrame(frame);
    }
    stop() {
        this.running = false;
        cancelAnimationFrame(this.rafId);
    }
    get isRunning() {
        return this.running;
    }
}
